import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../../components/Button";
import Input from "../../../components/Input";
import SelectInput from "../../../components/SelectInput";
import { Grid, Typography } from "@mui/material";
import { getCountryList } from "../../../services/Common";
import { login } from "../../../services/User";
import useToast from "../../../hooks/useToast";
import useValidation from "../../../hooks/useValidation";
import TrialSchema from "./ValidationSchema";
import PropTypes from "prop-types";
import { FireBaseContext } from "../../../contexts/FireBase";

const initialState = {
  countryCode: "+971",
  emailId: "",
  mobileNumber: "",
};

const userType = {
  SCHOOL_TEACHER: "School Teacher",
  SCHOOL_LEADER: "School Leader",
  PARENT: "School Parent",
  SYSTEM_ADMIN: "System Admin",
};

function EmailOrMobileVerification({ setActiveStep, setUserInfo, userInfo }) {
  const [callingCodes, setCallingCodes] = useState([]);
  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [isValid, setIsValid] = useState(false);
  const { validateProperty } = useValidation();
  const toast = useToast();
  const { t, i18n } = useTranslation();
  const { logEvent } = useContext(FireBaseContext);

  useEffect(() => {
    setCountryList();
  }, []);

  useEffect(() => {
    if (Object.keys(userInfo).length === 0) setFormData(initialState);
    else {
      setIsValid(true);
      setFormData(userInfo);
    }
  }, [userInfo]);

  const setCountryList = async () => {
    let codeList = [];
    try {
      const response = await getCountryList();
      if (response._statusCode === 200) {
        const { _entity: data } = response;
        if (data.length > 0)
          codeList = data.map((country) => country._callingCode);
        codeList.sort((a, b) => (a > b ? 1 : -1));
        codeList = [...new Set(codeList)];
        codeList = codeList.map((code) => ({
          value: code,
          label: (
            <span
              style={{
                direction: "ltr",
                display: "block",
                whiteSpace: "nowrap",
              }}
            >
              {code}
            </span>
          ),
        }));
        setCallingCodes(codeList);
      } else throw response;
    } catch (error) {
      console.log(error);
    }
  };

  const changeHandler = (event) => {
    const data = { ...formData };
    const errorsObject = {};
    const { currentTarget: input } = event;
    if (["mobileNumber", "emailId"].includes(input.name)) {
      input.value = input.value.toString().trim();
      const errorMessage = validateProperty(input, TrialSchema);
      if (errorMessage) errorsObject[input.name] = errorMessage;
      else delete errorsObject[input.name];
      setErrors(errorsObject);
      setIsValid(Object.keys(errorsObject).length === 0);
    }
    data[input.name] = input.value;
    setFormData(data);
  };

  const callingCodeChangeHandler = (event) => {
    setFormData({ ...formData, countryCode: event.currentTarget.value });
  };

  const formatText = (e) => {
    if (e.key === "Enter") return true;
    const NUMBER_DOT_COMMA = /^[\d]*$/;
    const fieldValue = e.target.value;
    if (!NUMBER_DOT_COMMA.test(e.key) || fieldValue.length > 15)
      e.preventDefault();
  };

  const handleSubmit = async () => {
    setIsValid(false);
    let _contactType = "email";
    let _emailOrMobile = formData.emailId;
    if (formData.mobileNumber.length) {
      _contactType = "mobile";
      const countryCode = formData.countryCode.replace("+", "");
      _emailOrMobile = countryCode + formData.mobileNumber;
    }
    try {
      const response = await login({
        _contactType,
        _emailOrMobile,
      });
      if (response) {
        if (response._statusCode === 200) {
          const {
            _entity: { _id: userId, _type: type },
          } = response;
          if (userId) {
            if (type === "SCHOOL_PRINCIPAL") {
              setActiveStep(2);
              setUserInfo({
                ...response._entity,
                countryCode: formData.countryCode,
                mobileNumber: formData.mobileNumber,
                emailId: formData.emailId,
              });
              logEvent("ClickOnLoginRequest", {
                Status: "Success",
                LoginType: _contactType,
              });
            } else if (userType[type]) {
              toast({
                label: t("Note"),
                message: t(userType[type]),
                rtl: i18n.dir() === "rtl",
                type: "info",
              });
              logEvent("ClickOnLoginRequest", {
                Status: "Failure",
                LoginType: _contactType,
              });
            }
          } else {
            toast({
              label: t("Error Label"),
              message: t("User Not Exist"),
              rtl: i18n.dir() === "rtl",
              type: "error",
            });
            logEvent("ClickOnLoginRequest", {
              Status: "Failure",
              LoginType: _contactType,
            });
          }
        } else if (response._statusCode === 400) {
          logEvent("ClickOnLoginRequest", {
            Status: "Failure",
            LoginType: _contactType,
          });
          toast({
            label: t("Note"),
            message: t(userType["PARENT"]),
            rtl: i18n.dir() === "rtl",
            type: "info",
          });
        }
      } else throw response;
    } catch (error) {
      console.log({ error });
    }
  };

  const formVars = {
    email: {
      name: "emailId",
      placeholder: t("Email Placeholder"),
      label: t("Email Label"),
      value: formData.emailId,
      onChange: changeHandler,
      disabled: formData.mobileNumber.length > 0,
    },
    mobile: {
      name: "mobileNumber",
      placeholder: t("Mobile Placeholder"),
      value: formData.mobileNumber,
      onChange: changeHandler,
      onKeyPress: formatText,
      disabled: formData.emailId.length > 0,
    },
    callingCode: {
      name: "countryCode",
      defaultValue: formData.countryCode,
      valueList: callingCodes,
      getOption: callingCodeChangeHandler,
      disabled: formData.emailId.length > 0,
    },
  };

  const isFormNotValid =
    (formData.emailId.length > 0 || formData.mobileNumber.length > 0) &&
    isValid;

  return (
    <Typography component="div" sx={{ textAlign: "center", width: "400px" }}>
      <Typography component="div" sx={{ width: "100%" }}>
        <p
          style={{
            fontSize: "16px",
            color: "rgba(34, 43, 68, 1)",
            whiteSpace: "nowrap",
          }}
        >
          {t("Sign In Heading")}
        </p>
      </Typography>
      <Typography component="div" sx={{ width: "100%" }}>
        <Typography
          component="div"
          sx={{
            display: "flex",
            flexDirection: i18n.dir() === "rtl" ? "row" : "row-reverse",
            gap: 1,
            paddingBottom: "35px",
            alignItems: "flex-end",
          }}
        >
          <Typography component="div" sx={{ width: "75%" }}>
            <Input
              {...formVars.mobile}
              label={i18n.dir() === "rtl" ? t("Mobile") : ""}
              errors={t(errors.mobileNumber)}
            />
          </Typography>
          <Typography component="div" sx={{ width: "25%" }}>
            <SelectInput
              {...formVars.callingCode}
              label={i18n.dir() === "ltr" ? t("Mobile") : ""}
            />
          </Typography>
        </Typography>
      </Typography>
      <Typography
        component="div"
        sx={{
          width: "100%",
          textAlign: "center",
          fontSize: "18px",
          color: "rgb(80, 80, 80)",
        }}
      >
        {t("OR")}
      </Typography>
      <Typography component="div" sx={{ paddingBottom: "35px" }}>
        <Input {...formVars.email} errors={t(errors.emailId)} />
      </Typography>
      <Typography component="div" sx={{ paddingBottom: "35px" }}>
        <Button
          label={t("Sign In")}
          disable={!isFormNotValid}
          action={handleSubmit}
        />
      </Typography>
    </Typography>
  );
}

EmailOrMobileVerification.propTypes = {
  setActiveStep: PropTypes.func.isRequired,
  setUserInfo: PropTypes.func.isRequired,
  userInfo: PropTypes.object.isRequired,
};

export default EmailOrMobileVerification;
